import React from "react";
import { Link } from "react-router-dom";
import { X, Link2, ArrowUpRight, Crosshair, Layers, Calculator, Truck, HeartPulse } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/common/Page";
import { useAuth } from "@/context/AuthContext";
import { nok, num, fmtDate } from "@/lib/format";
import { label as statusLabel } from "@/lib/labels";
import { Dot, LEVEL_DOT } from "./RigControls";
import { DELIVERY_COLORS, DELIVERY_LABELS, DELIVERY_ORDER, mnok } from "./rigGeometry";

export const DRAWER_TABS = [
  { id: "bom", label: "Materialliste", icon: Layers },
  { id: "kalkyle", label: "Kalkyle", icon: Calculator },
  { id: "leveranse", label: "Leveranseplan", icon: Truck },
  { id: "helse", label: "Design health", icon: HeartPulse },
];

const Th = ({ children, className }) => <th className={cn("px-3 py-1.5 text-left text-[11px] font-medium text-muted-foreground", className)}>{children}</th>;
const Td = ({ children, className, testId }) => <td className={cn("px-3 py-1.5 text-[12.5px]", className)} data-testid={testId}>{children}</td>;

/* ---------------- Materialliste ---------------- */
export function BomPanel({ ed }) {
  const { summary, focusModules, modules } = ed;
  const rows = summary.bom;
  const k = summary.kpi;
  return (
    <div className="flex h-full min-h-0" data-testid="rig-bom-panel">
      <div className="min-w-0 flex-1 overflow-auto scrollbar-thin">
        <table className="w-full border-separate border-spacing-0">
          <thead className="sticky top-0 bg-background/95 backdrop-blur">
            <tr>
              <Th>Modultype</Th>
              <Th className="text-right">Antall</Th>
              <Th className="text-right">Senger</Th>
              <Th className="text-right">Areal</Th>
              <Th className="text-right">Vekt</Th>
              <Th className="text-right">Leie / mnd</Th>
              <Th className="text-right">Enheter</Th>
              <Th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-[12.5px] text-muted-foreground" data-testid="rig-bom-empty">Ingen moduler i designet enda.</td></tr>}
            {rows.map((r) => (
              <tr key={r.module_type_id} className="group hover:bg-muted/50" data-testid={`rig-bom-row-${r.code}`}>
                <Td>
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-[2px] ring-1 ring-black/10" style={{ background: r.color }} aria-hidden />
                    <span className="font-mono text-[11.5px] text-muted-foreground">{r.code}</span>
                    <span className="truncate">{r.name}</span>
                  </div>
                </Td>
                <Td className="text-right font-medium tabular" testId={`rig-bom-qty-${r.code}`}>{r.quantity}</Td>
                <Td className="text-right tabular text-muted-foreground">{r.beds || "–"}</Td>
                <Td className="text-right tabular text-muted-foreground">{num(r.area_m2)} m²</Td>
                <Td className="text-right tabular text-muted-foreground">{num(r.weight_kg / 1000, 1)} t</Td>
                <Td className="text-right tabular">{nok(r.monthly_total)}</Td>
                <Td className="text-right tabular text-muted-foreground">{r.linked_assets} / {r.quantity}</Td>
                <Td className="text-right">
                  <button type="button" onClick={() => focusModules(modules.filter((m) => m.module_type_id === r.module_type_id).map((m) => m.id))} className="inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground opacity-0 transition-opacity duration-150 hover:bg-muted hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100" title="Vis i scenen" aria-label={`Vis ${r.code} i scenen`} data-testid={`rig-bom-focus-${r.code}`}><Crosshair className="h-3.5 w-3.5" /></button>
                </Td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="sticky bottom-0 bg-background/95 backdrop-blur">
              <tr className="text-[12.5px] font-medium">
                <Td>Sum</Td>
                <Td className="text-right tabular" testId="rig-bom-total-qty">{k.modules}</Td>
                <Td className="text-right tabular">{k.beds}</Td>
                <Td className="text-right tabular">{num(k.area_m2)} m²</Td>
                <Td className="text-right tabular">{num(k.weight_t, 1)} t</Td>
                <Td className="text-right tabular" testId="rig-bom-total-rent">{nok(k.monthly_rent)}</Td>
                <Td className="text-right tabular text-muted-foreground">{modules.filter((m) => m.asset_id).length} / {k.modules}</Td>
                <Td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="hidden w-64 shrink-0 border-l border-border/60 px-4 py-3 lg:block">
        <div className="text-[11px] font-medium text-muted-foreground">Logistikk fra listen</div>
        <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2.5">
          <Field label="Transporter" value={k.transports} />
          <Field label="Leveringsdager" value={k.delivery_days ? `≈ ${k.delivery_days}` : "–"} />
          <Field label="Montasje" value={`${num(k.assembly_hours)} t`} />
          <Field label="Totalvekt" value={`${num(k.weight_t, 1)} t`} />
        </dl>
        <p className="mt-4 text-[11px] leading-4 text-muted-foreground">Materiallisten er avledet direkte fra plasseringene i scenen og er grunnlaget for planlagt oppsett, transport og kalkyle.</p>
      </div>
    </div>
  );
}

/* ---------------- Kalkyle ---------------- */
export function CalcPanel({ ed }) {
  const { can } = useAuth();
  const { estimate, estimateLoading, calculation, applyToCalculation, applying, modules, project, summary } = ed;
  const canApply = can("calculations:write") && modules.length > 0;
  const locked = calculation && ["laast", "godkjent"].includes(calculation.status);
  const diff = estimate && calculation?.totals?.revenue != null ? estimate.revenue - calculation.totals.revenue : null;
  return (
    <div className="grid h-full min-h-0 grid-cols-1 gap-6 overflow-auto px-4 py-3 scrollbar-thin lg:grid-cols-[1fr_320px]" data-testid="rig-calc-panel">
      <div>
        <div className="flex items-baseline justify-between">
          <div className="text-[11px] font-medium text-muted-foreground">Estimat fra riggdesign</div>
          {estimateLoading && <span className="text-[11px] text-muted-foreground">Beregner…</span>}
        </div>
        {estimate ? (
          <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3" data-testid="rig-estimate">
            <Field label={`Kontraktsverdi (${estimate.duration_months} mnd)`}><span className="text-[17px] font-semibold tabular tracking-tight" data-testid="rig-estimate-revenue">{nok(estimate.revenue)}</span></Field>
            <Field label="Dekningsgrad"><span className="text-[17px] font-semibold tabular tracking-tight">{num(estimate.margin_pct, 1)} %</span></Field>
            <Field label="Månedsleie" value={nok(estimate.monthly_total)} />
            <Field label="Etablering / transport / montasje" value={nok(estimate.establishment_total)} />
            <Field label="Kostnad" value={nok(estimate.cost)} />
            <Field label="Kalkylelinjer" value={`${estimate.line_count} via kalkylemotor`} />
          </dl>
        ) : (
          <div className="mt-2 text-[12.5px] text-muted-foreground">{modules.length ? "Estimat beregnes…" : "Legg til moduler for å få et estimat."}</div>
        )}
        <p className="mt-4 max-w-prose text-[11.5px] leading-4 text-muted-foreground">
          Estimatet bruker prosjektets satser og modultypenes leie, etablering og montasjetimer – samme motor som kalkylen. Grunnlag: {summary.kpi.modules} moduler, {summary.kpi.transports} transporter, {num(summary.kpi.assembly_hours)} montasjetimer.
        </p>
      </div>
      <div className="rounded-md bg-muted/50 px-4 py-3 text-[12.5px] leading-5" data-testid="rig-calc-project">
        <div className="text-[11px] font-medium text-muted-foreground">Prosjektets kalkyle</div>
        {calculation ? (
          <div className="mt-1">
            <Link to={`/prosjekter/${project?.id}?tab=okonomi`} className="link font-mono" data-testid="rig-calc-link">{calculation.calc_number}</Link>
            <span className="text-muted-foreground"> · {statusLabel(calculation.status)}</span>
            {calculation.totals?.revenue != null && (
              <div className="mt-0.5 tabular">
                {nok(calculation.totals.revenue)}
                {diff != null && Math.abs(diff) > 1 && <span className={cn("ml-1.5 text-[12px]", diff > 0 ? "text-amber-700" : "text-emerald-700")} data-testid="rig-estimate-diff">({diff > 0 ? "+" : "−"}{nok(Math.abs(diff))} vs. design)</span>}
              </div>
            )}
            {calculation.source?.type === "rig_design" && <div className="text-[11.5px] text-muted-foreground">Sist oppdatert fra riggdesign v{calculation.source.version}</div>}
          </div>
        ) : (
          <div className="mt-1 text-muted-foreground">Prosjektet har ingen kalkyle enda – den opprettes ved overføring.</div>
        )}
        {canApply && (
          <Button className="mt-3 w-full" onClick={() => applyToCalculation({})} disabled={applying} data-testid="rig-apply-calc-button">
            {applying ? "Overfører…" : locked ? "Overfør som ny revisjon" : calculation ? "Overfør til kalkyle" : "Opprett kalkyle fra riggdesign"}
          </Button>
        )}
        <p className="mt-2 text-[11px] leading-4 text-muted-foreground">{locked ? "Kalkylen er låst; overføring lager en ny revisjon som prosjektet peker til." : "Overføring genererer kalkylelinjene på nytt fra satser og modultyper."}</p>
      </div>
    </div>
  );
}

/* ---------------- Leveranseplan ---------------- */
export function DeliveryPanel({ ed, compact = false }) {
  const { can } = useAuth();
  const { delivery, modules, linkAssets, linking, focusModules, setMode, deliveryById } = ed;
  const counts = delivery?.counts || {};
  const total = delivery?.total || modules.length;
  const unlinked = modules.filter((m) => !m.asset_id).length;
  const statuses = DELIVERY_ORDER.filter((s) => counts[s]);
  const installed = delivery?.installed ?? 0;
  const items = delivery?.items || [];
  return (
    <div className={cn("flex h-full min-h-0", compact ? "flex-col" : "flex-col lg:flex-row")} data-testid="rig-delivery-panel">
      <div className={cn("shrink-0 px-4 py-3", compact ? "" : "lg:w-72 lg:border-r lg:border-border/60")}>
        <div className="flex items-baseline justify-between">
          <div className="text-[12.5px] font-medium">Leveranse</div>
          <div className="text-[12px] text-muted-foreground tabular" data-testid="rig-delivery-progress-text">{installed} / {total} montert</div>
        </div>
        <div className="mt-2 flex h-1.5 w-full overflow-hidden rounded-full bg-muted" aria-hidden>
          {statuses.map((s) => <div key={s} style={{ width: `${(counts[s] / Math.max(1, total)) * 100}%`, background: DELIVERY_COLORS[s] }} />)}
        </div>
        <ul className="mt-3 space-y-0.5">
          {statuses.map((s) => (
            <li key={s}>
              <button type="button" onClick={() => { focusModules(items.filter((it) => it.delivery_status === s).map((it) => it.id)); setMode("delivery"); }} className="flex w-full items-center justify-between rounded px-1.5 py-1 text-[12.5px] hover:bg-muted/70" data-testid={`rig-delivery-status-${s}`}>
                <span className="inline-flex items-center gap-2"><Dot color={DELIVERY_COLORS[s]} />{DELIVERY_LABELS[s]}</span>
                <span className="tabular text-muted-foreground">{counts[s]}</span>
              </button>
            </li>
          ))}
          {!statuses.length && <li className="py-4 text-center text-[12.5px] text-muted-foreground">Ingen leveransestatus enda.</li>}
        </ul>
        {unlinked > 0 && <p className="mt-3 text-[11.5px] leading-4 text-muted-foreground"><span className="text-foreground">{unlinked} plasseringer</span> mangler fysisk enhet.</p>}
        {can("planning:write") && unlinked > 0 && (
          <Button variant="outline" size="sm" className="mt-2 w-full" onClick={() => linkAssets()} disabled={linking} data-testid="rig-link-assets-button"><Link2 /> {linking ? "Kobler…" : "Koble allokerte enheter"}</Button>
        )}
      </div>
      <div className="min-h-0 min-w-0 flex-1 overflow-auto scrollbar-thin">
        <table className="w-full border-separate border-spacing-0">
          <thead className="sticky top-0 bg-background/95 backdrop-blur">
            <tr>
              <Th>Plassering</Th>
              <Th>Enhet</Th>
              <Th>Status</Th>
              {!compact && <Th>Sted</Th>}
              {!compact && <Th>Transport</Th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-[12.5px] text-muted-foreground">Ingen plasseringer.</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { focusModules([it.id]); setMode("delivery"); }} data-testid={`rig-delivery-item-${it.label}`}>
                <Td><span className="font-mono text-[12px]">{it.label}</span></Td>
                <Td>{it.asset_code ? <Link to={`/moduler/${it.asset_id}`} onClick={(e) => e.stopPropagation()} className="link font-mono text-[12px]">{it.asset_code}</Link> : <span className="text-muted-foreground">–</span>}</Td>
                <Td><span className="inline-flex items-center gap-1.5"><Dot color={DELIVERY_COLORS[it.delivery_status]} />{it.delivery_label}</span></Td>
                {!compact && <Td className="text-muted-foreground">{it.location || "–"}</Td>}
                {!compact && <Td className="text-muted-foreground">{it.transport ? <span className="font-mono text-[12px]">{it.transport.transport_number} <span className="font-sans">· {fmtDate(it.transport.date)}</span></span> : "–"}</Td>}
              </tr>
            ))}
          </tbody>
        </table>
        {!compact && deliveryById && <div className="px-3 py-2 text-[11px] text-muted-foreground">Klikk en rad for å finne plasseringen i scenen. Fargene i scenen følger leveransestatus i Levering-modus.</div>}
      </div>
    </div>
  );
}

/* ---------------- Design health ---------------- */
export function HealthPanel({ ed, compact = false }) {
  const { health, focusModules } = ed;
  const order = { error: 0, warn: 1, info: 2, ok: 3 };
  const checks = [...health.checks].sort((a, b) => order[a.level] - order[b.level]);
  return (
    <div className={cn("h-full min-h-0 overflow-auto scrollbar-thin", compact ? "" : "px-2 py-2")} data-testid="rig-health-panel">
      {!compact && (
        <div className="flex items-baseline justify-between px-2 pb-1">
          <div className="text-[12.5px] font-medium">Automatisk validering</div>
          <div className="text-[11.5px] text-muted-foreground tabular" data-testid="rig-health-summary">{health.errors ? `${health.errors} feil · ` : ""}{health.warnings} merknader · {checks.filter((c) => c.level === "ok").length} OK</div>
        </div>
      )}
      <ul className={cn(compact ? "space-y-0.5" : "grid gap-x-6 gap-y-0.5 lg:grid-cols-2")}>
        {checks.map((c) => (
          <li key={c.id} className="flex items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-muted/50" data-testid={`rig-health-${c.id}`}>
            <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", LEVEL_DOT[c.level])} aria-hidden />
            <div className="min-w-0 flex-1">
              <div className={cn("text-[12.5px] leading-5", c.level === "ok" ? "text-muted-foreground" : "text-foreground")}>{c.title}</div>
              {c.detail && <div className="text-[11.5px] leading-4 text-muted-foreground">{c.detail}</div>}
            </div>
            {c.moduleIds?.length > 0 && (
              <button type="button" onClick={() => focusModules(c.moduleIds)} className="inline-flex h-6 shrink-0 items-center gap-1 rounded px-1.5 text-[11.5px] text-muted-foreground hover:bg-muted hover:text-foreground" data-testid={`rig-health-focus-${c.id}`}>
                <Crosshair className="h-3.5 w-3.5" /> Vis {c.moduleIds.length}
              </button>
            )}
          </li>
        ))}
      </ul>
      {!compact && <p className="px-2 pt-3 text-[11px] leading-4 text-muted-foreground">Reglene sjekker geometri (kollisjon, understøttelse), adkomst (gang/trapp), teknisk forsyning og kapasitet mot prosjektets behov. De er veiledende for prosjektering – ikke en erstatning for byggteknisk kontroll.</p>}
    </div>
  );
}

/** Bottom drawer that overlays the canvas column. Tabs are the four rig-wide views. */
export function RigDrawer({ ed, tab, onTab, onClose, className, height = 300 }) {
  const active = DRAWER_TABS.find((t) => t.id === tab) || DRAWER_TABS[0];
  return (
    <div className={cn("absolute inset-x-0 bottom-0 z-30 flex flex-col overflow-hidden border-t border-border/70 bg-background/95 shadow-pop backdrop-blur-md", className)} style={{ height }} data-testid="rig-drawer" role="region" aria-label={active.label}>
      <div className="flex h-9 shrink-0 items-center gap-1 px-2">
        {DRAWER_TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => onTab(t.id)} aria-pressed={tab === t.id}
            className={cn("inline-flex h-7 items-center gap-1.5 rounded-md px-2.5 text-[12px] font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50", tab === t.id ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground")}
            data-testid={`rig-drawer-tab-${t.id}`}>
            <t.icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {t.label}
            {t.id === "helse" && (ed.health.errors > 0 || ed.health.warnings > 0) && <span className={cn("ml-0.5 h-1.5 w-1.5 rounded-full", ed.health.errors ? "bg-rose-500" : "bg-amber-500")} aria-hidden />}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-1">
          {tab === "bom" && <Link to={`/prosjekter/${ed.project?.id}?tab=moduler`} className="inline-flex h-7 items-center gap-1 rounded-md px-2 text-[11.5px] text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="rig-drawer-open-project">Prosjektets moduler <ArrowUpRight className="h-3 w-3" /></Link>}
          <button type="button" onClick={onClose} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Lukk panel" data-testid="rig-drawer-close"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="min-h-0 flex-1">
        {tab === "bom" && <BomPanel ed={ed} />}
        {tab === "kalkyle" && <CalcPanel ed={ed} />}
        {tab === "leveranse" && <DeliveryPanel ed={ed} />}
        {tab === "helse" && <HealthPanel ed={ed} />}
      </div>
    </div>
  );
}

export const healthTone = (health) => (health.errors ? "error" : health.warnings ? "warn" : "ok");
export const healthText = (health) => (health.errors ? `${health.errors} feil` : health.warnings ? `${health.warnings} merknader` : "Design OK");
export { mnok };
